import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { apiBlob, apiFetch } from './apiFetch';
import './App.css';
import Map from './components/Map';

const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [tablets, setTablets] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTablet, setNewTablet] = useState({ 
    name: '', 
    location: '', 
    code: ''
  });
  const [loading, setLoading] = useState(true);
  const [presenceClock, setPresenceClock] = useState(Date.now());
  const [socket, setSocket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'online', 'offline'
  const [sortOrder, setSortOrder] = useState('none'); // 'none' = sem ordenação, 'asc' = mais antigo primeiro, 'desc' = mais recente primeiro
  const [showConsoleModal, setShowConsoleModal] = useState(false);
  const [selectedTablet, setSelectedTablet] = useState(null);
  const [jsCommand, setJsCommand] = useState('');
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [selectedTabletForUrl, setSelectedTabletForUrl] = useState(null);
  const [newUrl, setNewUrl] = useState('');
  const [showAutoReloadModal, setShowAutoReloadModal] = useState(false);
  const [selectedTabletForAutoReload, setSelectedTabletForAutoReload] = useState(null);
  const [autoReloadInterval, setAutoReloadInterval] = useState('60');
  const [showBrightnessModal, setShowBrightnessModal] = useState(false);
  const [selectedTabletForBrightness, setSelectedTabletForBrightness] = useState(null);
  const [screenBrightness, setScreenBrightness] = useState(100);
  const [showAutoCodModal, setShowAutoCodModal] = useState(false);
  const [selectedTabletForAutoCod, setSelectedTabletForAutoCod] = useState(null);
  const [autoCodRules, setAutoCodRules] = useState([]);
  const [loadingAutoCodRules, setLoadingAutoCodRules] = useState(false);
  const [autoCodForm, setAutoCodForm] = useState({ triggerUrl: '', savedCommandId: '' });
  const [showEditTabletModal, setShowEditTabletModal] = useState(false);
  const [editingTabletOriginalCode, setEditingTabletOriginalCode] = useState('');
  const [editingTablet, setEditingTablet] = useState({ code: '', name: '', location: '' });
  const [savedCommands, setSavedCommands] = useState([]);
  const [showSavedCommands, setShowSavedCommands] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSelectedCodes, setBulkSelectedCodes] = useState([]);
  const [bulkActiveAction, setBulkActiveAction] = useState('reload'); // reload | js | url | clear
  const [bulkJsCommand, setBulkJsCommand] = useState('');
  const [bulkUrl, setBulkUrl] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [showBulkSavedCommands, setShowBulkSavedCommands] = useState(false);
  const [actionMenuCode, setActionMenuCode] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, right: 0 });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedTabletForPassword, setSelectedTabletForPassword] = useState(null);
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [showDeviceConsoleModal, setShowDeviceConsoleModal] = useState(false);
  const [selectedTabletForConsoleLogs, setSelectedTabletForConsoleLogs] = useState(null);
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [loadingConsoleLogs, setLoadingConsoleLogs] = useState(false);
  const [visibleConsoleCount, setVisibleConsoleCount] = useState(10);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [consoleTimeFrom, setConsoleTimeFrom] = useState('');
  const [consoleTimeTo, setConsoleTimeTo] = useState('');
  const [consoleArchiveDate, setConsoleArchiveDate] = useState('');
  const [showWifiModal, setShowWifiModal] = useState(false);
  const [selectedTabletForWifi, setSelectedTabletForWifi] = useState(null);
  const [wifiForm, setWifiForm] = useState({ ssid: '', password: '', securityType: 'WPA-PSK' });
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [selectedTabletForPhotos, setSelectedTabletForPhotos] = useState(null);
  const [tabletPhotos, setTabletPhotos] = useState([]);
  const [photoImageUrls, setPhotoImageUrls] = useState({});
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [showPrintscreenModal, setShowPrintscreenModal] = useState(false);
  const [selectedTabletForPrintscreen, setSelectedTabletForPrintscreen] = useState(null);
  const [tabletPrintscreens, setTabletPrintscreens] = useState([]);
  const [printscreenImageUrls, setPrintscreenImageUrls] = useState({});
  const [loadingPrintscreens, setLoadingPrintscreens] = useState(false);
  const selectedTabletForConsoleLogsRef = useRef(null);
  const selectedTabletForPhotosRef = useRef(null);
  const photoImageUrlsRef = useRef({});
  const selectedTabletForPrintscreenRef = useRef(null);
  const printscreenImageUrlsRef = useRef({});
  const { user, logout, token } = useAuth();

  useEffect(() => {
    const timer = setInterval(() => setPresenceClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const request = (path, options = {}) => apiFetch(`${API_URL}${path}`, options, token);
  const requestBlob = (path) => apiBlob(`${API_URL}${path}`, token);
  const clearPhotoImageUrls = () => {
    Object.values(photoImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    photoImageUrlsRef.current = {};
    setPhotoImageUrls({});
  };
  const clearPrintscreenImageUrls = () => {
    Object.values(printscreenImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    printscreenImageUrlsRef.current = {};
    setPrintscreenImageUrls({});
  };

  const loadSavedCommands = async () => {
    try {
      const response = await request('/api/saved-commands');
      setSavedCommands(response);
    } catch (error) {
      console.error('Erro ao carregar comandos salvos:', error);
    }
  };

  const toggleBulkSelected = (code) => {
    setBulkSelectedCodes((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      return [...prev, code];
    });
  };

  const bulkSelectAll = (codes) => {
    setBulkSelectedCodes(Array.from(new Set(codes)));
  };

  const bulkClearSelection = () => {
    setBulkSelectedCodes([]);
  };

  const openBulkModal = (action, defaultCodes) => {
    setBulkActiveAction(action);
    setShowBulkModal(true);
    setShowBulkSavedCommands(false);
    bulkSelectAll(defaultCodes);
  };

  const handleLoadCommandIntoBulk = (cmd) => {
    setBulkJsCommand(cmd.command || '');
    setBulkActiveAction('js');
    setShowBulkSavedCommands(false);
  };

  const bulkSendReload = async () => {
    const codes = bulkSelectedCodes;
    if (codes.length === 0) {
      alert('❌ Selecione ao menos 1 tablet');
      return;
    }
    if (!window.confirm(`Enviar RELOAD para ${codes.length} dispositivo(s)?`)) {
      return;
    }

    setBulkSending(true);
    try {
      await Promise.all(codes.map((code) => request(`/api/tablets/${code}/request-reload`, { method: 'POST' })));
      alert(`✅ Reload solicitado para ${codes.length} dispositivo(s)`);
    } catch (error) {
      console.error('Erro ao enviar reload em lote:', error);
      alert('❌ Erro ao enviar reload em lote');
    } finally {
      setBulkSending(false);
    }
  };

  const bulkSendJs = async () => {
    const codes = bulkSelectedCodes;
    if (codes.length === 0) {
      alert('❌ Selecione ao menos 1 tablet');
      return;
    }
    if (!bulkJsCommand.trim()) {
      alert('❌ Digite um comando JS');
      return;
    }
    if (!window.confirm(`Enviar comando JS para ${codes.length} dispositivo(s)?`)) {
      return;
    }

    setBulkSending(true);
    try {
      await Promise.all(codes.map((code) => request(`/api/tablets/${code}/execute-js`, {
        method: 'POST',
        body: { execute_js: bulkJsCommand.trim() }
      })));
      alert(`✅ Comando JS enviado para ${codes.length} dispositivo(s)`);
    } catch (error) {
      console.error('Erro ao enviar JS em lote:', error);
      alert('❌ Erro ao enviar JS em lote');
    } finally {
      setBulkSending(false);
    }
  };

  const bulkSendUrl = async () => {
    const codes = bulkSelectedCodes;
    if (codes.length === 0) {
      alert('❌ Selecione ao menos 1 tablet');
      return;
    }
    if (!bulkUrl.trim()) {
      alert('❌ Digite a URL');
      return;
    }
    if (!window.confirm(`Enviar URL para ${codes.length} dispositivo(s)?`)) {
      return;
    }

    setBulkSending(true);
    try {
      await Promise.all(codes.map((code) => request(`/api/tablets/${code}`, {
        method: 'PUT',
        body: { homepage_url: bulkUrl.trim() }
      })));
      alert(`✅ URL enviada para ${codes.length} dispositivo(s)`);
    } catch (error) {
      console.error('Erro ao enviar URL em lote:', error);
      alert('❌ Erro ao enviar URL em lote');
    } finally {
      setBulkSending(false);
    }
  };

  const bulkSendClearStorage = async () => {
    const codes = bulkSelectedCodes;
    if (codes.length === 0) {
      alert('❌ Selecione ao menos 1 tablet');
      return;
    }
    if (!window.confirm(`Limpar storage/cache em ${codes.length} dispositivo(s)?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    setBulkSending(true);
    try {
      await Promise.all(codes.map((code) => request(`/api/tablets/${code}/clear-storage`, { method: 'POST' })));
      alert(`✅ Limpeza solicitada para ${codes.length} dispositivo(s)`);
    } catch (error) {
      console.error('Erro ao limpar storage em lote:', error);
      alert('❌ Erro ao solicitar limpeza em lote');
    } finally {
      setBulkSending(false);
    }
  };

  const formatClock = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleString('pt-BR');
  };

  const handleOpenPasswordModal = (tablet) => {
    setSelectedTabletForPassword(tablet);
    setNewAdminPassword('');
    setShowPasswordModal(true);
    setActionMenuCode(null);
  };

  const handleSetPassword = async () => {
    if (!newAdminPassword.trim()) {
      alert('\u274c A senha n\u00e3o pode estar em branco');
      return;
    }
    if (!window.confirm(`Definir nova senha admin para "${selectedTabletForPassword.name}"?\n\nA senha será enviada imediatamente ao dispositivo conectado.`)) {
      return;
    }
    try {
      await request(`/api/tablets/${selectedTabletForPassword.code}/set-password`, {
        method: 'POST',
        body: {
          new_admin_password: newAdminPassword.trim()
        }
      });
      alert('✅ Nova senha enviada ao dispositivo');
      setShowPasswordModal(false);
      setNewAdminPassword('');
    } catch (error) {
      console.error('Erro ao definir senha:', error);
      alert('\u274c Erro ao definir nova senha');
    }
  };

  useEffect(() => {
    selectedTabletForConsoleLogsRef.current = selectedTabletForConsoleLogs;
  }, [selectedTabletForConsoleLogs]);

  useEffect(() => {
    selectedTabletForPhotosRef.current = selectedTabletForPhotos;
  }, [selectedTabletForPhotos]);

  useEffect(() => {
    selectedTabletForPrintscreenRef.current = selectedTabletForPrintscreen;
  }, [selectedTabletForPrintscreen]);

  useEffect(() => {
    photoImageUrlsRef.current = photoImageUrls;
  }, [photoImageUrls]);

  useEffect(() => {
    printscreenImageUrlsRef.current = printscreenImageUrls;
  }, [printscreenImageUrls]);

  useEffect(() => {
    return () => {
      Object.values(photoImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      Object.values(printscreenImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (token) {
      // Carregar comandos salvos após configurar o token
      loadSavedCommands();
    }

    // Conectar ao WebSocket - se API_URL estiver vazio, conecta ao mesmo host/origem
    const socketConnection = API_URL
      ? io(API_URL, { path: '/api/controlremote' })
      : io({ path: '/api/controlremote' });
    setSocket(socketConnection);

    // Carregar tablets iniciais
    loadTablets();

    // Depuração: conexões do socket
    socketConnection.on('connect', () => {
      console.log('[Socket] connected, id=', socketConnection.id);
    });
    socketConnection.on('disconnect', (reason) => {
      console.log('[Socket] disconnected, reason=', reason);
    });
    socketConnection.on('connect_error', (err) => {
      console.error('[Socket] connect_error', err);
    });

    // Escutar atualizações em tempo real (com log para debug)
    socketConnection.on('tablet:updated', (updatedTablet) => {
      console.log('[Socket] tablet:updated', updatedTablet);
      setTablets(prev => 
        prev.map(t => t.code === updatedTablet.code ? updatedTablet : t)
      );
    });

    socketConnection.on('tablet:console-log', (logEntry) => {
      setConsoleLogs(prev => {
        const selectedConsoleTablet = selectedTabletForConsoleLogsRef.current;
        if (!selectedConsoleTablet || logEntry.tabletCode !== selectedConsoleTablet.code) {
          return prev;
        }
        if (prev.some(log => log._id === logEntry._id || log.clientLogId === logEntry.clientLogId)) {
          return prev;
        }
        // prepend newest logs so the most recent appears at the top
        const next = [logEntry, ...prev].slice(0, 1000);
        return next;
      });
    });

    socketConnection.on('tablet:console-cleared', (event) => {
      setConsoleLogs(prev => {
        const selectedConsoleTablet = selectedTabletForConsoleLogsRef.current;
        if (!selectedConsoleTablet || event.tabletCode !== selectedConsoleTablet.code) {
          return prev;
        }
        return [];
      });
    });

    socketConnection.on('tablet:photo-updated', (photo) => {
      const selectedPhotoTablet = selectedTabletForPhotosRef.current;
      if (!selectedPhotoTablet || photo.tabletCode !== selectedPhotoTablet.code) {
        return;
      }
      loadTabletPhotos(selectedPhotoTablet.code);
    });

    socketConnection.on('tablet:photo-deleted', (event) => {
      const selectedPhotoTablet = selectedTabletForPhotosRef.current;
      if (!selectedPhotoTablet || event.tabletCode !== selectedPhotoTablet.code) {
        return;
      }
      setTabletPhotos(prev => prev.filter(photo => photo._id !== event.photoId));
    });

    socketConnection.on('tablet:photos-cleared', (event) => {
      const selectedPhotoTablet = selectedTabletForPhotosRef.current;
      if (!selectedPhotoTablet || event.tabletCode !== selectedPhotoTablet.code) {
        return;
      }
      setTabletPhotos([]);
    });

    socketConnection.on('tablet:printscreen-updated', (printscreen) => {
      const selectedPrintscreenTablet = selectedTabletForPrintscreenRef.current;
      if (!selectedPrintscreenTablet || printscreen.tabletCode !== selectedPrintscreenTablet.code) {
        return;
      }
      loadTabletPrintscreens(selectedPrintscreenTablet.code);
    });

    socketConnection.on('tablet:printscreen-deleted', (event) => {
      const selectedPrintscreenTablet = selectedTabletForPrintscreenRef.current;
      if (!selectedPrintscreenTablet || event.tabletCode !== selectedPrintscreenTablet.code) {
        return;
      }
      setTabletPrintscreens(prev => prev.filter(item => item._id !== event.printscreenId));
    });

    socketConnection.on('tablet:printscreens-cleared', (event) => {
      const selectedPrintscreenTablet = selectedTabletForPrintscreenRef.current;
      if (!selectedPrintscreenTablet || event.tabletCode !== selectedPrintscreenTablet.code) {
        return;
      }
      setTabletPrintscreens([]);
      clearPrintscreenImageUrls();
    });

    return () => {
      socketConnection.disconnect();
    };
  }, [token]);

  const loadTablets = async () => {
    try {
      setLoading(true);
      const response = await request('/api/tablets');
      setTablets(response);
    } catch (error) {
      console.error('Erro ao carregar tablets:', error);
      alert('Erro ao carregar tablets');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTablet = async (e) => {
    e.preventDefault();
    try {
      const response = await request('/api/tablets', {
        method: 'POST',
        body: newTablet
      });
      setTablets([response, ...tablets]);
      setNewTablet({ 
        name: '', 
        location: '', 
        code: ''
      });
      setShowAddModal(false);
      alert(`Tablet cadastrado com sucesso!\nCódigo: ${response.code}\n\nConfigure este código no app Android.`);
    } catch (error) {
      console.error('Erro ao cadastrar tablet:', error);
      alert('Erro ao cadastrar tablet');
    }
  };

  const handleDeleteTablet = async (code) => {
    if (!window.confirm('Tem certeza que deseja remover este tablet?')) {
      return;
    }
    try {
      await request(`/api/tablets/${code}`, { method: 'DELETE' });
      setTablets(tablets.filter(t => t.code !== code));
    } catch (error) {
      console.error('Erro ao deletar tablet:', error);
      alert('Erro ao deletar tablet');
    }
  };

  const handleOpenBrightnessModal = (tablet) => {
    setActionMenuCode(null);
    setSelectedTabletForBrightness(tablet);
    setScreenBrightness(tablet.screen_brightness ?? 100);
    setShowBrightnessModal(true);
  };

  const handleSetBrightness = async () => {
    if (!selectedTabletForBrightness) return;
    const brightness = Math.max(0, Math.min(100, Number(screenBrightness) || 0));
    try {
      await request(`/api/tablets/${selectedTabletForBrightness.code}/set-brightness`, {
        method: 'POST',
        body: { brightness }
      });
      setTablets(prev => prev.map(tablet =>
        tablet.code === selectedTabletForBrightness.code
          ? { ...tablet, screen_brightness: brightness, brightness_pending: true }
          : tablet
      ));
      setShowBrightnessModal(false);
    } catch (error) {
      console.error('Erro ao alterar brilho:', error);
      alert('Erro ao alterar brilho da tela');
    }
  };

  const loadAutoCodRules = async (code) => {
    setLoadingAutoCodRules(true);
    try {
      const rules = await request(`/api/tablets/${code}/auto-cod`);
      setAutoCodRules(Array.isArray(rules) ? rules : []);
    } catch (error) {
      console.error('Erro ao carregar Auto Cod:', error);
      alert('Erro ao carregar Auto Cod');
    } finally {
      setLoadingAutoCodRules(false);
    }
  };

  const handleOpenAutoCodModal = async (tablet) => {
    setActionMenuCode(null);
    setSelectedTabletForAutoCod(tablet);
    setAutoCodForm({
      triggerUrl: '',
      savedCommandId: ''
    });
    setAutoCodRules([]);
    setShowAutoCodModal(true);
    await loadAutoCodRules(tablet.code);
  };

  const handleAddAutoCodRule = async () => {
    if (!selectedTabletForAutoCod) return;
    if (!autoCodForm.triggerUrl.trim()) {
      alert('Informe o link que deve ser detectado');
      return;
    }
    if (!autoCodForm.savedCommandId) {
      alert('Selecione um comando salvo');
      return;
    }

    try {
      const rule = await request(`/api/tablets/${selectedTabletForAutoCod.code}/auto-cod`, {
        method: 'POST',
        body: {
          triggerUrl: autoCodForm.triggerUrl.trim(),
          savedCommandId: autoCodForm.savedCommandId,
          enabled: true
        }
      });
      setAutoCodRules(prev => [...prev, rule]);
      setTablets(prev => prev.map(tablet =>
        tablet.code === selectedTabletForAutoCod.code
          ? { ...tablet, auto_cod_rules: [...(tablet.auto_cod_rules || []), rule] }
          : tablet
      ));
      setAutoCodForm(prev => ({ ...prev, triggerUrl: '' }));
    } catch (error) {
      console.error('Erro ao adicionar Auto Cod:', error);
      alert(error.message || 'Erro ao adicionar Auto Cod');
    }
  };

  const handleToggleAutoCodRule = async (rule) => {
    if (!selectedTabletForAutoCod) return;
    try {
      const updatedRule = await request(`/api/tablets/${selectedTabletForAutoCod.code}/auto-cod/${rule._id}`, {
        method: 'PUT',
        body: { enabled: !rule.enabled }
      });
      setAutoCodRules(prev => prev.map(item => item._id === rule._id ? updatedRule : item));
      setTablets(prev => prev.map(tablet =>
        tablet.code === selectedTabletForAutoCod.code
          ? {
              ...tablet,
              auto_cod_rules: (tablet.auto_cod_rules || []).map(item =>
                item._id === rule._id ? updatedRule : item
              )
            }
          : tablet
      ));
    } catch (error) {
      console.error('Erro ao alterar Auto Cod:', error);
      alert('Erro ao alterar Auto Cod');
    }
  };

  const handleDeleteAutoCodRule = async (rule) => {
    if (!selectedTabletForAutoCod) return;
    if (!window.confirm('Remover esta regra Auto Cod?')) return;

    try {
      await request(`/api/tablets/${selectedTabletForAutoCod.code}/auto-cod/${rule._id}`, {
        method: 'DELETE'
      });
      setAutoCodRules(prev => prev.filter(item => item._id !== rule._id));
      setTablets(prev => prev.map(tablet =>
        tablet.code === selectedTabletForAutoCod.code
          ? { ...tablet, auto_cod_rules: (tablet.auto_cod_rules || []).filter(item => item._id !== rule._id) }
          : tablet
      ));
    } catch (error) {
      console.error('Erro ao remover Auto Cod:', error);
      alert('Erro ao remover Auto Cod');
    }
  };

  const handleOpenEditTabletModal = (tablet) => {
    setActionMenuCode(null);
    setEditingTabletOriginalCode(tablet.code);
    setEditingTablet({
      code: tablet.code,
      name: tablet.name || '',
      location: tablet.location || ''
    });
    setShowEditTabletModal(true);
  };

  const handleUpdateTabletDetails = async (e) => {
    e.preventDefault();
    const nextCode = editingTablet.code.trim().toUpperCase();
    if (
      nextCode !== editingTabletOriginalCode &&
      !window.confirm(`Alterar o codigo do dispositivo de ${editingTabletOriginalCode} para ${nextCode}?`)
    ) {
      return;
    }
    try {
      const updatedTablet = await request(`/api/tablets/${editingTabletOriginalCode}`, {
        method: 'PUT',
        body: {
          code: nextCode,
          name: editingTablet.name.trim(),
          location: editingTablet.location.trim()
        }
      });
      setTablets(prev => prev.map(tablet =>
        tablet.code === editingTabletOriginalCode ? updatedTablet : tablet
      ));
      setShowEditTabletModal(false);
      setEditingTabletOriginalCode('');
    } catch (error) {
      console.error('Erro ao atualizar dispositivo:', error);
      alert(error.message || 'Erro ao atualizar dispositivo');
    }
  };

  const handleRequestReload = async (code, tabletName) => {
    if (!window.confirm(`Deseja solicitar reload da página no tablet "${tabletName}"?`)) {
      return;
    }
    try {
      await request(`/api/tablets/${code}/request-reload`, { method: 'POST' });
      alert(`Reload enviado com sucesso para "${tabletName}".`);
    } catch (error) {
      console.error('Erro ao solicitar reload:', error);
      alert('Erro ao solicitar reload');
    }
  };

  const handleUpdateReloadInterval = async (code, newInterval) => {
    const interval = Math.max(0, parseInt(newInterval, 10) || 0);
    try {
      await request(`/api/tablets/${code}`, {
        method: 'PUT',
        body: {
          reload_interval: interval
        }
      });
      setTablets(prev => prev.map(t => 
        t.code === code ? { ...t, reload_interval: interval } : t
      ));
      return true;
    } catch (error) {
      console.error('Erro ao atualizar intervalo de reload:', error);
      alert('Erro ao atualizar intervalo de reload');
      return false;
    }
  };

  const handleOpenAutoReloadModal = (tablet) => {
    setActionMenuCode(null);
    setSelectedTabletForAutoReload(tablet);
    setAutoReloadInterval(String(tablet.reload_interval ?? 60));
    setShowAutoReloadModal(true);
  };

  const handleSaveAutoReload = async () => {
    if (!selectedTabletForAutoReload) return;
    const saved = await handleUpdateReloadInterval(
      selectedTabletForAutoReload.code,
      autoReloadInterval
    );
    if (saved) {
      setShowAutoReloadModal(false);
      setSelectedTabletForAutoReload(null);
    }
  };

  const handleUpdatePingInterval = async (code, newInterval) => {
    const interval = Math.max(10, parseInt(newInterval) || 60);
    try {
      await request(`/api/tablets/${code}`, {
        method: 'PUT',
        body: {
          ping_interval_seconds: interval
        }
      });
      setTablets(prev => prev.map(t =>
        t.code === code ? { ...t, ping_interval_seconds: interval } : t
      ));
    } catch (error) {
      console.error('Erro ao atualizar intervalo de ping:', error);
      alert('Erro ao atualizar intervalo de ping');
    }
  };

  const handleUpdateKeepConsoleLogs = async (tablet, keepLogs) => {
    if (!keepLogs) {
      const confirmed = window.confirm(
        `Desmarcar "Manter logs ativos" para "${tablet.name}"?\n\nPossivelmente todos os logs podem sumir caso a página seja recarregada.`
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      await request(`/api/tablets/${tablet.code}`, {
        method: 'PUT',
        body: {
          keep_console_logs: keepLogs
        }
      });
      setTablets(prev => prev.map(t =>
        t.code === tablet.code ? { ...t, keep_console_logs: keepLogs } : t
      ));
    } catch (error) {
      console.error('Erro ao atualizar preservação de logs:', error);
      alert('Erro ao atualizar configuração de logs');
    }
  };

  const handleExecuteJs = (code, tabletName) => {
    setSelectedTablet({ code, name: tabletName });
    setJsCommand('');
    setShowConsoleModal(true);
  };

  const loadDeviceConsoleLogs = async (tablet, filters = {}) => {
    setLoadingConsoleLogs(true);
    setVisibleConsoleCount(10);

    try {
      const params = new URLSearchParams({ limit: '500' });
      if (filters.from) params.set('from', new Date(filters.from).toISOString());
      if (filters.to) params.set('to', new Date(filters.to).toISOString());

      const response = await request(`/api/tablets/${tablet.code}/console-logs?${params.toString()}`);
      // keep most recent first
      const logs = (response || []).slice().reverse();
      setConsoleLogs(logs);
    } catch (error) {
      console.error('Erro ao carregar console remoto:', error);
      alert('Erro ao carregar console remoto');
    } finally {
      setLoadingConsoleLogs(false);
    }
  };

  const handleOpenDeviceConsole = async (tablet) => {
    setSelectedTabletForConsoleLogs(tablet);
    setConsoleLogs([]);
    setShowDeviceConsoleModal(true);
    setActionMenuCode(null);
    setConsoleTimeFrom('');
    setConsoleTimeTo('');
    setConsoleArchiveDate('');
    await loadDeviceConsoleLogs(tablet);
  };

  const handleOpenWifiModal = (tablet) => {
    setSelectedTabletForWifi(tablet);
    setWifiForm({ ssid: '', password: '', securityType: 'WPA-PSK' });
    setShowWifiModal(true);
    setActionMenuCode(null);
  };

  const loadTabletPhotos = async (code) => {
    try {
      setLoadingPhotos(true);
      const response = await request(`/api/tablets/${code}/photos`);
      const photos = response || [];
      setTabletPhotos(photos);
      await loadPhotoImages(photos);
    } catch (error) {
      console.error('Erro ao carregar fotos:', error);
      alert('Erro ao carregar fotos');
    } finally {
      setLoadingPhotos(false);
    }
  };

  const loadPhotoImages = async (photos) => {
    const capturedPhotos = photos.filter((photo) => photo.url);
    const entries = await Promise.all(capturedPhotos.map(async (photo) => {
      try {
        const blob = await requestBlob(photo.url);
        return [photo._id, URL.createObjectURL(blob)];
      } catch (error) {
        console.error('Erro ao carregar imagem protegida:', error);
        return [photo._id, ''];
      }
    }));

    Object.values(photoImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    const nextUrls = Object.fromEntries(entries.filter(([, url]) => url));
    photoImageUrlsRef.current = nextUrls;
    setPhotoImageUrls(nextUrls);
  };

  const handleOpenPhotosModal = async (tablet) => {
    setSelectedTabletForPhotos(tablet);
    setTabletPhotos([]);
    clearPhotoImageUrls();
    setShowPhotosModal(true);
    setActionMenuCode(null);
    await loadTabletPhotos(tablet.code);
  };

  const handleRequestPhoto = async () => {
    if (!selectedTabletForPhotos) return;
    try {
      await request(`/api/tablets/${selectedTabletForPhotos.code}/capture-photo`, { method: 'POST' });
      await loadTabletPhotos(selectedTabletForPhotos.code);
    } catch (error) {
      console.error('Erro ao solicitar foto:', error);
      alert(error.data?.error || 'Erro ao solicitar foto');
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!selectedTabletForPhotos) return;
    if (!window.confirm('Remover esta foto?')) return;

    try {
      await request(`/api/tablets/${selectedTabletForPhotos.code}/photos/${photo._id}`, { method: 'DELETE' });
      setTabletPhotos(prev => prev.filter(item => item._id !== photo._id));
      setPhotoImageUrls(prev => {
        if (prev[photo._id]) URL.revokeObjectURL(prev[photo._id]);
        const next = { ...prev };
        delete next[photo._id];
        photoImageUrlsRef.current = next;
        return next;
      });
    } catch (error) {
      console.error('Erro ao deletar foto:', error);
      alert('Erro ao deletar foto');
    }
  };

  const handleDeleteAllPhotos = async () => {
    if (!selectedTabletForPhotos) return;
    if (!window.confirm(`Remover todas as fotos de "${selectedTabletForPhotos.name}"?`)) return;

    try {
      await request(`/api/tablets/${selectedTabletForPhotos.code}/photos`, { method: 'DELETE' });
      setTabletPhotos([]);
      clearPhotoImageUrls();
    } catch (error) {
      console.error('Erro ao deletar fotos:', error);
      alert('Erro ao deletar fotos');
    }
  };

  const loadTabletPrintscreens = async (code) => {
    try {
      setLoadingPrintscreens(true);
      const response = await request(`/api/tablets/${code}/printscreens`);
      const printscreens = response || [];
      setTabletPrintscreens(printscreens);
      await loadPrintscreenImages(printscreens);
    } catch (error) {
      console.error('Erro ao carregar printscreens:', error);
      alert('Erro ao carregar printscreens');
    } finally {
      setLoadingPrintscreens(false);
    }
  };

  const loadPrintscreenImages = async (printscreens) => {
    const captured = printscreens.filter((item) => item.url);
    const entries = await Promise.all(captured.map(async (item) => {
      try {
        const blob = await requestBlob(item.url);
        return [item._id, URL.createObjectURL(blob)];
      } catch (error) {
        console.error('Erro ao carregar printscreen protegido:', error);
        return [item._id, ''];
      }
    }));

    Object.values(printscreenImageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    const nextUrls = Object.fromEntries(entries.filter(([, url]) => url));
    printscreenImageUrlsRef.current = nextUrls;
    setPrintscreenImageUrls(nextUrls);
  };

  const handleOpenPrintscreenModal = async (tablet) => {
    setSelectedTabletForPrintscreen(tablet);
    setTabletPrintscreens([]);
    clearPrintscreenImageUrls();
    setShowPrintscreenModal(true);
    setActionMenuCode(null);
    await loadTabletPrintscreens(tablet.code);
  };

  const handleRequestPrintscreen = async () => {
    if (!selectedTabletForPrintscreen) return;
    try {
      await request(`/api/tablets/${selectedTabletForPrintscreen.code}/capture-printscreen`, { method: 'POST' });
      await loadTabletPrintscreens(selectedTabletForPrintscreen.code);
    } catch (error) {
      console.error('Erro ao solicitar printscreen:', error);
      alert(error.data?.error || 'Erro ao solicitar printscreen');
    }
  };

  const handleDeletePrintscreen = async (printscreen) => {
    if (!selectedTabletForPrintscreen) return;
    if (!window.confirm('Remover este printscreen?')) return;

    try {
      await request(`/api/tablets/${selectedTabletForPrintscreen.code}/printscreens/${printscreen._id}`, { method: 'DELETE' });
      setTabletPrintscreens(prev => prev.filter(item => item._id !== printscreen._id));
      setPrintscreenImageUrls(prev => {
        if (prev[printscreen._id]) URL.revokeObjectURL(prev[printscreen._id]);
        const next = { ...prev };
        delete next[printscreen._id];
        printscreenImageUrlsRef.current = next;
        return next;
      });
    } catch (error) {
      console.error('Erro ao deletar printscreen:', error);
      alert('Erro ao deletar printscreen');
    }
  };

  const handleDeleteAllPrintscreens = async () => {
    if (!selectedTabletForPrintscreen) return;
    if (!window.confirm(`Remover todos os printscreens de "${selectedTabletForPrintscreen.name}"?`)) return;

    try {
      await request(`/api/tablets/${selectedTabletForPrintscreen.code}/printscreens`, { method: 'DELETE' });
      setTabletPrintscreens([]);
      clearPrintscreenImageUrls();
    } catch (error) {
      console.error('Erro ao deletar printscreens:', error);
      alert('Erro ao deletar printscreens');
    }
  };

  const handleRequestApkUpdate = async (tablet) => {
    const status = tablet.apk_update_status?.status;
    const activeStatuses = ['pending', 'delivered', 'checking', 'downloading', 'installing'];
    if (tablet.apk_update_request_id || activeStatuses.includes(status)) {
      alert('Ja existe uma atualizacao APK em andamento para este dispositivo.');
      return;
    }

    if (!window.confirm(`Solicitar atualização do app no kiosk "${tablet.name}"?\n\nO comando será enviado imediatamente ao dispositivo conectado.`)) {
      return;
    }

    try {
      await request(`/api/tablets/${tablet.code}/request-apk-update`, { method: 'POST' });
      alert('Atualização APK solicitada.');
    } catch (error) {
      console.error('Erro ao solicitar atualizacao APK:', error);
      alert(error.data?.error || 'Erro ao solicitar atualizacao APK');
    }
  };

  const handleClearApkUpdateStatus = async (tablet) => {
    if (!window.confirm(`Limpar o status de atualizacao APK de "${tablet.name}"?\n\nIsso remove apenas o status visual e o registro pendente no banco.`)) {
      return;
    }

    try {
      await request(`/api/tablets/${tablet.code}/apk-update/clear`, { method: 'POST' });
    } catch (error) {
      console.error('Erro ao limpar status APK:', error);
      alert(error.data?.error || 'Erro ao limpar status APK');
    }
  };

  const handleSendWifiCommand = async (action) => {
    if (!selectedTabletForWifi) return;
    if (action === 'disconnect') {
      const confirmed = window.confirm(
        `Desconectar o WiFi de "${selectedTabletForWifi.name}"?\n\nSe o dispositivo ficar sem rede, o remote_kiosk pode perder contato com ele até alguém reconectar localmente ou ele entrar em outra rede automaticamente.`
      );
      if (!confirmed) return;
    }
    if (action === 'connect' && !wifiForm.ssid.trim()) {
      alert('Informe o SSID da rede WiFi');
      return;
    }

    try {
      await request(`/api/tablets/${selectedTabletForWifi.code}/wifi-command`, {
        method: 'POST',
        body: {
          action,
          ssid: wifiForm.ssid.trim(),
          password: wifiForm.password,
          securityType: wifiForm.securityType
        }
      });
      alert(action === 'connect'
        ? 'Comando de conexão WiFi enviado.'
        : 'Comando de desconexão WiFi enviado.');
      setShowWifiModal(false);
      setSelectedTabletForWifi(null);
    } catch (error) {
      console.error('Erro ao enviar comando WiFi:', error);
      alert('Erro ao enviar comando WiFi');
    }
  };

  const handleCloseDeviceConsole = () => {
    setShowDeviceConsoleModal(false);
    setSelectedTabletForConsoleLogs(null);
    setConsoleLogs([]);
    setConsoleTimeFrom('');
    setConsoleTimeTo('');
    setConsoleArchiveDate('');
  };

  const handleApplyConsoleTimeFilter = () => {
    if (!selectedTabletForConsoleLogs) return;
    loadDeviceConsoleLogs(selectedTabletForConsoleLogs, {
      from: consoleTimeFrom,
      to: consoleTimeTo
    });
  };

  const handleDownloadConsoleArchive = async () => {
    if (!selectedTabletForConsoleLogs || !consoleArchiveDate) {
      alert('Escolha uma data para baixar os logs arquivados');
      return;
    }

    try {
      const blob = await requestBlob(`/api/tablets/${selectedTabletForConsoleLogs.code}/console-logs/download?date=${consoleArchiveDate}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedTabletForConsoleLogs.name}-${consoleArchiveDate}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar logs arquivados:', error);
      alert(error.message || 'Erro ao baixar logs arquivados');
    }
  };

  const handleDeviceConsoleScroll = (e) => {
    const el = e.currentTarget;
    if (loadingMoreLogs || loadingConsoleLogs) return;
    // when user scrolls near bottom, load more (older) logs
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      if (visibleConsoleCount < consoleLogs.length) {
        setLoadingMoreLogs(true);
        // simulate async load; here logs are already fetched, so just increase visible count
        setTimeout(() => {
          setVisibleConsoleCount((c) => Math.min(consoleLogs.length, c + 10));
          setLoadingMoreLogs(false);
        }, 250);
      }
    }
  };

  const handleSendJsCommand = async () => {
    if (!jsCommand.trim() || !selectedTablet) {
      return;
    }

    try {
      await request(`/api/tablets/${selectedTablet.code}/execute-js`, {
        method: 'POST',
        body: {
          execute_js: jsCommand.trim()
        }
      });
      alert(`✅ Comando enviado para "${selectedTablet.name}"`);
      setShowConsoleModal(false);
      setJsCommand('');
      setSelectedTablet(null);
    } catch (error) {
      console.error('Erro ao enviar comando JavaScript:', error);
      alert('❌ Erro ao enviar comando JavaScript');
    }
  };

  const handleOpenUrlModal = (tablet) => {
    setSelectedTabletForUrl(tablet);
    setNewUrl(tablet.homepage_url || '');
    setShowUrlModal(true);
  };

  const handleUpdateUrl = async () => {
    if (!selectedTabletForUrl) {
      return;
    }

    try {
      await request(`/api/tablets/${selectedTabletForUrl.code}`, {
        method: 'PUT',
        body: {
          homepage_url: newUrl.trim()
        }
      });
      // Atualizar localmente
      setTablets(prev => prev.map(t => 
        t.code === selectedTabletForUrl.code ? { ...t, homepage_url: newUrl.trim() } : t
      ));
      alert(`✅ URL enviada para "${selectedTabletForUrl.name}"`);
      setShowUrlModal(false);
      setNewUrl('');
      setSelectedTabletForUrl(null);
    } catch (error) {
      console.error('Erro ao atualizar URL:', error);
      alert('❌ Erro ao atualizar URL');
    }
  };

  const handleClearStorage = async (code, tabletName) => {
    if (!window.confirm(`Deseja limpar LocalStorage, Cookies e Cache do tablet "${tabletName}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await request(`/api/tablets/${code}/clear-storage`, { method: 'POST' });
      alert(`✅ Limpeza de storage enviada para "${tabletName}"`);
    } catch (error) {
      console.error('Erro ao solicitar limpeza de storage:', error);
      alert('❌ Erro ao solicitar limpeza de storage');
    }
  };

  const handleSaveCommand = async () => {
    if (!jsCommand.trim()) {
      alert('❌ Digite um comando para salvar');
      return;
    }
    
    const commandName = prompt('Digite um nome para este comando:');
    if (!commandName || !commandName.trim()) {
      return;
    }
    
    try {
      const response = await request('/api/saved-commands', {
        method: 'POST',
        body: {
          name: commandName.trim(),
          command: jsCommand.trim()
        }
      });
      
      setSavedCommands([response, ...savedCommands]);
      alert(`✅ Comando "${commandName}" salvo com sucesso!`);
    } catch (error) {
      console.error('Erro ao salvar comando:', error);
      alert('❌ Erro ao salvar comando');
    }
  };

  const handleLoadCommand = (command) => {
    setJsCommand(command.command);
    setShowSavedCommands(false);
  };

  const handleDeleteSavedCommand = async (commandId) => {
    const command = savedCommands.find(c => c._id === commandId);
    if (!window.confirm(`Deseja deletar o comando "${command.name}"?`)) {
      return;
    }
    
    try {
      await request(`/api/saved-commands/${commandId}`, { method: 'DELETE' });
      setSavedCommands(savedCommands.filter(c => c._id !== commandId));
    } catch (error) {
      console.error('Erro ao deletar comando:', error);
      alert('❌ Erro ao deletar comando');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Nunca';
    const d = new Date(date);
    return d.toLocaleString('pt-BR');
  };

  const formatConsoleTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getPhotoImageUrl = (photo) => {
    if (!photo?._id) return '';
    return photoImageUrls[photo._id] || '';
  };

  const getPrintscreenImageUrl = (printscreen) => {
    if (!printscreen?._id) return '';
    return printscreenImageUrls[printscreen._id] || '';
  };

  const getApkUpdateStatus = (tablet) => {
    const rawStatus = tablet.apk_update_request_id ? 'pending' : tablet.apk_update_status?.status;
    if (!rawStatus) return null;

    const labels = {
      pending: 'Aguardando dispositivo',
      delivered: 'Entregue ao kiosk',
      checking: 'Verificando APK',
      downloading: 'Baixando APK',
      installing: 'Instalando',
      success: 'Atualizado',
      failed: 'Falhou',
      skipped: 'Ignorado'
    };

    return {
      status: rawStatus,
      label: labels[rawStatus] || rawStatus,
      message: tablet.apk_update_status?.message || '',
      updatedAt: tablet.apk_update_status?.updatedAt || null
    };
  };

  const getDeviceVersionLabel = (tablet) => {
    const versionName = tablet.deviceInfo?.appVersion;
    const versionCode = tablet.deviceInfo?.appVersionCode;
    if (!versionName && versionCode === undefined) return '';
    if (versionName && versionCode !== undefined) return `v${versionName} (${versionCode})`;
    return versionName ? `v${versionName}` : `(${versionCode})`;
  };

  const getTimeSinceLastPing = (lastPing) => {
    if (!lastPing) return 'Nunca';
    const now = new Date();
    const ping = new Date(lastPing);
    const diffMs = now - ping;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Agora';
    if (diffMins === 1) return '1 minuto atrás';
    if (diffMins < 60) return `${diffMins} minutos atrás`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hora atrás';
    if (diffHours < 24) return `${diffHours} horas atrás`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return '1 dia atrás';
    return `${diffDays} dias atrás`;
  };

  const getDisconnectedDuration = (disconnectedAt) => {
    if (!disconnectedAt) return 'Desconectado';
    const totalSeconds = Math.max(0, Math.floor((presenceClock - new Date(disconnectedAt).getTime()) / 1000));
    if (totalSeconds < 60) return `Desconectado há ${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    if (minutes < 60) return `Desconectado há ${minutes}min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Desconectado há ${hours}h ${minutes % 60}min`;
    const days = Math.floor(hours / 24);
    return `Desconectado há ${days}d ${hours % 24}h`;
  };

  const stats = {
    total: tablets.length,
    online: tablets.filter(t => t.status === 'online').length,
    offline: tablets.filter(t => t.status === 'offline').length
  };

  // Filtrar e ordenar tablets
  const getFilteredAndSortedTablets = () => {
    // Filtrar por status
    let filtered = tablets;
    if (filterStatus === 'online') {
      filtered = tablets.filter(t => t.status === 'online');
    } else if (filterStatus === 'offline') {
      filtered = tablets.filter(t => t.status === 'offline');
    }

    // Se sortOrder for 'none', retorna sem ordenação (ordem original)
    if (sortOrder === 'none') {
      return filtered;
    }

    // Ordenar por lastPing
    const sorted = [...filtered].sort((a, b) => {
      const dateA = a.lastPing ? new Date(a.lastPing).getTime() : 0;
      const dateB = b.lastPing ? new Date(b.lastPing).getTime() : 0;
      
      if (sortOrder === 'desc') {
        return dateB - dateA; // Mais recente primeiro
      } else {
        return dateA - dateB; // Mais antigo primeiro
      }
    });

    return sorted;
  };

  const filteredTablets = getFilteredAndSortedTablets();

  // Função para alternar entre os 3 estados de ordenação
  const toggleSortOrder = () => {
    if (sortOrder === 'none') {
      setSortOrder('desc'); // none -> desc (↑)
    } else if (sortOrder === 'desc') {
      setSortOrder('asc'); // desc -> asc (↓)
    } else {
      setSortOrder('none'); // asc -> none (sem setinha)
    }
  };

  return (
    <div className="App">
      <header className="dashboard-header">
        <div className="header-title">
          <h1>TABLETS ONLINE</h1>
        </div>
        <div className="header-actions">
          <button className="btn-add" onClick={() => setShowAddModal(true)}>
            + Cadastrar Novo
          </button>
          <a 
            href="/downloads/app-debug.apk" 
            download="kiosk-app.apk"
            className="btn-download-apk"
          >
            📱 Baixar APK
          </a>
          <button className="btn-logout" onClick={logout}>
            Sair
          </button>
        </div>
      </header>

      <main className="dashboard-container">
        <div className="dashboard-sidebar">
          {/* Stats Cards */}
          <div className="stats-section">
            <div 
              className={`stat-card stat-card-total ${filterStatus === 'all' ? 'stat-card-active' : ''}`}
              onClick={() => setFilterStatus('all')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-label">TOTAL DE LINKS</div>
              <div className="stat-value">{stats.total}</div>
            </div>
            <div 
              className={`stat-card stat-card-online ${filterStatus === 'online' ? 'stat-card-active' : ''}`}
              onClick={() => setFilterStatus('online')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-label">LINKS ONLINE</div>
              <div className="stat-value">{stats.online}</div>
            </div>
            <div 
              className={`stat-card stat-card-offline ${filterStatus === 'offline' ? 'stat-card-active' : ''}`}
              onClick={() => setFilterStatus('offline')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-label">LINKS OFFLINE</div>
              <div className="stat-value">{stats.offline}</div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="chart-section">
            <div className="chart-title">VISÃO GERAL</div>
            <div className="pie-chart">
              <svg viewBox="0 0 100 100" className="pie">
                {stats.total > 0 && (
                  <>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="#00BCD4"
                      strokeWidth="20"
                      strokeDasharray={`${(stats.online / stats.total) * 251.2} 251.2`}
                      transform="rotate(-90 50 50)"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="#FF5252"
                      strokeWidth="20"
                      strokeDasharray={`${(stats.offline / stats.total) * 251.2} 251.2`}
                      strokeDashoffset={`-${(stats.online / stats.total) * 251.2}`}
                      transform="rotate(-90 50 50)"
                    />
                  </>
                )}
              </svg>
              <div className="pie-label">
                {stats.total > 0 ? Math.round((stats.online / stats.total) * 100) : 0}%
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="map-section">
            <Map tablets={tablets} />
          </div>
        </div>

        <div className="dashboard-main">
          <div className="devices-header">
            <h2>Dispositivos</h2>
            <div className="devices-filters">
              <div className="bulk-action-buttons" title="Ações em lote (selecionar tablets e enviar comandos)">
                <button
                  className="btn-reload bulk-btn"
                  onClick={() => openBulkModal('reload', filteredTablets.map(t => t.code))}
                  title="Ações em lote: Reload"
                >
                  🔄
                </button>
                <button
                  className="btn-js bulk-btn"
                  onClick={() => openBulkModal('js', filteredTablets.map(t => t.code))}
                  title="Ações em lote: JavaScript"
                >
                  📜
                </button>
                <button
                  className="btn-url bulk-btn"
                  onClick={() => openBulkModal('url', filteredTablets.map(t => t.code))}
                  title="Ações em lote: URL"
                >
                  🔗
                </button>
                <button
                  className="btn-clear-storage bulk-btn"
                  onClick={() => openBulkModal('clear', filteredTablets.map(t => t.code))}
                  title="Ações em lote: Limpar storage"
                >
                  🗑️
                </button>
              </div>
              <button 
                className="filter-btn active" 
                onClick={toggleSortOrder}
              >
                Status {sortOrder === 'none' ? '' : sortOrder === 'desc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">Carregando...</div>
          ) : filteredTablets.length === 0 ? (
            <div className="empty-state">
              <p>
                {tablets.length === 0 
                  ? 'Nenhum tablet cadastrado ainda.' 
                  : `Nenhum tablet ${filterStatus === 'online' ? 'online' : filterStatus === 'offline' ? 'offline' : ''} encontrado.`
                }
              </p>
              {tablets.length === 0 && (
                <button onClick={() => setShowAddModal(true)}>Cadastrar Primeiro Tablet</button>
              )}
            </div>
          ) : (
            <div className="devices-list">
              <div className="devices-table">
                <div className="table-header">
                  <div className="col-name">Dispositivo</div>
                  <div className="col-status">Status</div>
                  <div className="col-telemetry">Telemetria</div>
                  <div className="col-ping">última conexão</div>
                  <div className="col-actions">Ações</div>
                </div>
                {filteredTablets.map(tablet => (
                  <div key={tablet.code} className={`device-row ${tablet.status}`}>
                    <div className="col-name" data-label="Dispositivo">
                      <div className="device-title-row">
                        <div className="device-name">{tablet.name}</div>
                      </div>
                      <div className="device-location">{tablet.location || 'Sem localização'}</div>
                      <div className="device-code">Código: {tablet.code}</div>
                    </div>
                    <div className="col-status" data-label="Status">
                      <span className={`status-badge ${tablet.status}`}>
                        {tablet.status === 'online' ? 'Online' : 'Offline'}
                      </span>
                      <div className="device-version">{getDeviceVersionLabel(tablet) || 'Nao informada'}</div>
                      {getApkUpdateStatus(tablet) && (
                        <div className={`apk-update-status apk-update-status--${getApkUpdateStatus(tablet).status}`}>
                          <strong>{getApkUpdateStatus(tablet).label}</strong>
                          {getApkUpdateStatus(tablet).message && (
                            <span>{getApkUpdateStatus(tablet).message}</span>
                          )}
                          {getApkUpdateStatus(tablet).updatedAt && (
                            <small>{formatDate(getApkUpdateStatus(tablet).updatedAt)}</small>
                          )}
                          <button
                            className="apk-update-clear"
                            onClick={() => handleClearApkUpdateStatus(tablet)}
                            title="Limpar status da atualizacao no banco e na tela"
                          >
                            Limpar status
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="col-telemetry" data-label="Telemetria">
                      {tablet.deviceInfo ? (
                        <div className="telemetry-info">
                          {tablet.deviceInfo.wifiName && (
                            <div className="telemetry-item">
                              <span className="telemetry-icon">📶</span>
                              <span className="telemetry-label">WiFi:</span>
                              <span className="telemetry-value">{tablet.deviceInfo.wifiName}</span>
                            </div>
                          )}
                          {tablet.deviceInfo.macAddress && (
                            <div className="telemetry-item">
                              <span className="telemetry-icon">🔌</span>
                              <span className="telemetry-label">MAC:</span>
                              <span className="telemetry-value">{tablet.deviceInfo.macAddress}</span>
                            </div>
                          )}
                          {tablet.deviceInfo.batteryLevel !== undefined && (
                            <div className={`telemetry-item battery-item ${
                              tablet.deviceInfo.batteryLevel <= 30 ? 'battery-critical' : 
                              !tablet.deviceInfo.isCharging ? 'battery-warning' : ''
                            }`}>
                              <span className="telemetry-icon">
                                {tablet.deviceInfo.isCharging ? '⚡' : '🔋'}
                              </span>
                              <span className="telemetry-label">Bateria:</span>
                              <span className="telemetry-value">
                                {tablet.deviceInfo.batteryLevel}%
                                {tablet.deviceInfo.isCharging && ' (Carregando)'}
                              </span>
                            </div>
                          )}
                          {tablet.coordinates?.latitude && tablet.coordinates?.longitude && (
                            <div className="telemetry-item">
                              <span className="telemetry-icon">📍</span>
                              <span className="telemetry-label">GPS:</span>
                              <span className="telemetry-value">
                                {tablet.coordinates.latitude.toFixed(4)}, {tablet.coordinates.longitude.toFixed(4)}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="telemetry-info">
                          <span className="telemetry-empty">Sem dados</span>
                        </div>
                      )}
                    </div>
                    <div className="col-ping" data-label="Ultima conexao">
                      <span>
                        {tablet.status === 'offline'
                          ? getDisconnectedDuration(tablet.disconnectedAt || tablet.lastPing)
                          : 'Agora'}
                      </span>
                    </div>
                    <div className="col-actions" data-label="Acoes">
                      <div className="actions-group">
                        <label className="keep-logs-control" title="Mantém os logs remotos mesmo após reload da página no kiosk">
                          <input
                            type="checkbox"
                            checked={tablet.keep_console_logs === true}
                            onChange={(e) => handleUpdateKeepConsoleLogs(tablet, e.target.checked)}
                          />
                          <span>Manter logs ativos</span>
                        </label>
                        <div className="action-buttons">
                          <button
                            className="btn-action-toggle"
                            onClick={(e) => {
                              if (actionMenuCode === tablet.code) {
                                setActionMenuCode(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setActionMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                                setActionMenuCode(tablet.code);
                              }
                            }}
                            title="Ações disponíveis para este dispositivo"
                          >
                            ⚙️ Ações
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ---- Action Menu Popup ---- */}
      {actionMenuCode && (() => {
        const t = tablets.find(x => x.code === actionMenuCode);
        if (!t) return null;
        return (
          <>
            <div className="action-menu-backdrop" onClick={() => setActionMenuCode(null)} />
            <div className="action-popup" role="dialog" aria-modal="true">
              <div className="action-popup-header">{t.name}</div>
              <div className="action-popup-body">
                <button
                  className="action-menu-item"
                  onClick={() => { setActionMenuCode(null); handleRequestReload(t.code, t.name); }}
                  disabled={t.solicitacao_reload === 1}
                >
                  🔄 Reload
                  {t.solicitacao_reload === 1 && <span className="menu-indicator">⏳</span>}
                  {t.last_reload_delivered_at && t.solicitacao_reload !== 1 && <span className="menu-indicator">🕒</span>}
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => handleOpenAutoReloadModal(t)}
                >
                  Auto-reload
                  <span className="menu-indicator">
                    {(t.reload_interval ?? 60) > 0 ? `${t.reload_interval ?? 60} min` : 'OFF'}
                  </span>
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => handleOpenBrightnessModal(t)}
                >
                  Brilho da tela
                  <span className="menu-indicator">{t.screen_brightness ?? 100}%</span>
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => handleOpenAutoCodModal(t)}
                >
                  Auto Cod
                  {(t.auto_cod_rules || []).length > 0 && (
                    <span className="menu-indicator">{(t.auto_cod_rules || []).length}</span>
                  )}
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => { setActionMenuCode(null); handleExecuteJs(t.code, t.name); }}
                >
                  📜 Console JS
                  {t.execute_js ? <span className="menu-indicator">⏳</span> : t.last_execute_js_delivered_at ? <span className="menu-indicator">🕒</span> : null}
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => handleOpenDeviceConsole(t)}
                >
                  🖥️ Ver Console
                </button>
              <button
                className="action-menu-item"
                onClick={() => handleOpenWifiModal(t)}
              >
                📶 WiFi
                {t.wifi_command?.action ? <span className="menu-indicator">⏳</span> : t.last_wifi_command_delivered_at ? <span className="menu-indicator">🕒</span> : null}
              </button>
              <button
                className="action-menu-item"
                onClick={() => handleOpenPrintscreenModal(t)}
              >
                PrintScreen
                {t.printscreen_request_id ? <span className="menu-indicator">⏳</span> : t.last_printscreen_delivered_at ? <span className="menu-indicator">🕒</span> : null}
              </button>
              <button
                className="action-menu-item"
                onClick={() => handleOpenPhotosModal(t)}
                >
                  📷 Fotos
                  {t.capture_photo_request_id ? <span className="menu-indicator">⏳</span> : t.last_capture_photo_delivered_at ? <span className="menu-indicator">🕒</span> : null}
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => { setActionMenuCode(null); handleRequestApkUpdate(t); }}
                  disabled={t.apk_update_request_id || ['pending', 'delivered', 'checking', 'downloading', 'installing'].includes(t.apk_update_status?.status)}
                  title={t.apk_update_status?.message || 'Solicitar atualizacao do APK'}
                >
                  Atualizar APK
                  {(t.apk_update_request_id || ['pending', 'delivered', 'checking', 'downloading', 'installing'].includes(t.apk_update_status?.status)) ? <span className="menu-indicator">â³</span> : t.apk_update_status?.updatedAt ? <span className="menu-indicator">ðŸ•’</span> : null}
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => { setActionMenuCode(null); handleOpenUrlModal(t); }}
                >
                  🔗 Alterar URL
                  {t.homepage_url_pending === 1 ? <span className="menu-indicator">⏳</span> : t.last_homepage_url_delivered_at ? <span className="menu-indicator">🕒</span> : null}
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => { setActionMenuCode(null); handleClearStorage(t.code, t.name); }}
                >
                  🗑️ Limpar Storage
                  {t.clear_storage === 1 ? <span className="menu-indicator">⏳</span> : t.last_clear_storage_delivered_at ? <span className="menu-indicator">🕒</span> : null}
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => handleOpenPasswordModal(t)}
                >
                  🔑 Trocar Senha Admin
                </button>
                <button
                  className="action-menu-item"
                  onClick={() => handleOpenEditTabletModal(t)}
                >
                  Editar dispositivo
                </button>
                <div className="action-menu-divider" />
                <button
                  className="action-menu-item action-menu-item--danger"
                  onClick={() => { setActionMenuCode(null); handleDeleteTablet(t.code); }}
                >
                  × Remover Dispositivo
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {showConsoleModal && selectedTablet && (
        <div className="modal-overlay" onClick={() => setShowConsoleModal(false)}>
          <div className="console-modal" onClick={(e) => e.stopPropagation()}>
            <div className="console-header">
              <span>📜 Console JavaScript - {selectedTablet.name}</span>
              <button className="console-close" onClick={() => setShowConsoleModal(false)}>×</button>
            </div>
            <div className="console-body">
              <div className="console-info">
                <span className="console-prompt">~$</span> Digite o comando JavaScript abaixo:
              </div>
              <textarea 
                className="console-input"
                value={jsCommand}
                onChange={(e) => setJsCommand(e.target.value)}
                placeholder="console.log('Hello from Remote Kiosk');\nalert('Teste');\ndocument.title = 'Novo Título';"
                rows="8"
                autoFocus
                onKeyDown={(e) => {
                  if (e.ctrlKey && e.key === 'Enter') {
                    handleSendJsCommand();
                  }
                }}
              />
              <div className="console-footer">
                <div className="console-footer-left">
                  <button className="btn-console-saved" onClick={() => setShowSavedCommands(!showSavedCommands)}>
                    📋 Comandos Salvos ({savedCommands.length})
                  </button>
                  <button className="btn-console-save" onClick={handleSaveCommand} disabled={!jsCommand.trim()}>
                    💾 Salvar Comando
                  </button>
                </div>
                <div className="console-actions">
                  <button className="btn-console-cancel" onClick={() => setShowConsoleModal(false)}>
                    Cancelar
                  </button>
                  <button className="btn-console-send" onClick={handleSendJsCommand} disabled={!jsCommand.trim()}>
                    🚀 Enviar Comando
                  </button>
                </div>
              </div>
              {showSavedCommands && (
                <div className="saved-commands-list">
                  <div className="saved-commands-header">📋 Comandos Salvos</div>
                  {savedCommands.length === 0 ? (
                    <div className="saved-commands-empty">Nenhum comando salvo ainda</div>
                  ) : (
                    <div className="saved-commands-items">
                      {savedCommands.map(cmd => (
                        <div key={cmd._id} className="saved-command-item">
                          <div className="saved-command-info" onClick={() => handleLoadCommand(cmd)}>
                            <div className="saved-command-name">{cmd.name}</div>
                            <div className="saved-command-preview">{cmd.command.substring(0, 60)}{cmd.command.length > 60 ? '...' : ''}</div>
                          </div>
                          <button className="btn-delete-saved" onClick={() => handleDeleteSavedCommand(cmd._id)} title="Deletar comando">
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeviceConsoleModal && selectedTabletForConsoleLogs && (
        <div className="modal-overlay" onClick={handleCloseDeviceConsole}>
          <div className="device-console-modal" onClick={(e) => e.stopPropagation()}>
            <div className="console-header">
              <span>🖥️ Console do dispositivo - {selectedTabletForConsoleLogs.name}</span>
              <button className="console-close" onClick={handleCloseDeviceConsole}>×</button>
            </div>
              <div className="device-console-body">
                <div className="device-console-toolbar">
                  <div className="device-console-filter">
                    <label>
                      De
                      <input
                        type="datetime-local"
                        value={consoleTimeFrom}
                        onChange={(e) => setConsoleTimeFrom(e.target.value)}
                      />
                    </label>
                    <label>
                      Até
                      <input
                        type="datetime-local"
                        value={consoleTimeTo}
                        onChange={(e) => setConsoleTimeTo(e.target.value)}
                      />
                    </label>
                    <button className="btn-console-saved" onClick={handleApplyConsoleTimeFilter}>
                      Buscar horário
                    </button>
                    <button className="btn-console-cancel" onClick={() => {
                      setConsoleTimeFrom('');
                      setConsoleTimeTo('');
                      loadDeviceConsoleLogs(selectedTabletForConsoleLogs);
                    }}>
                      Limpar filtro
                    </button>
                  </div>
                  <div className="device-console-download">
                    <input
                      type="date"
                      value={consoleArchiveDate}
                      onChange={(e) => setConsoleArchiveDate(e.target.value)}
                    />
                    <button className="btn-console-send" onClick={handleDownloadConsoleArchive}>
                      Baixar TXT do dia
                    </button>
                  </div>
                </div>
                {loadingConsoleLogs ? (
                  <div className="device-console-empty">Carregando console...</div>
                ) : consoleLogs.length === 0 ? (
                  <div className="device-console-empty">Nenhuma mensagem recebida ainda</div>
                ) : (
                  <div className="device-console-stream" onScroll={handleDeviceConsoleScroll}>
                    {consoleLogs.slice(0, visibleConsoleCount).map(log => (
                      <div key={log._id || log.clientLogId} className={`device-console-line level-${log.level || 'log'}`}>
                        <span className="device-console-time">{formatConsoleTime(log.createdAt)}</span>
                        <span className="device-console-level">{log.level || 'log'}</span>
                        <span className="device-console-message">{log.message}</span>
                        {log.sourceId && (
                          <span className="device-console-source">
                            {log.sourceId}{log.lineNumber ? `:${log.lineNumber}` : ''}
                          </span>
                        )}
                      </div>
                    ))}
                    {loadingMoreLogs && (
                      <div className="device-console-loading">Carregando mais...</div>
                    )}
                  </div>
                )}
              </div>
          </div>
        </div>
      )}

      {showWifiModal && selectedTabletForWifi && (
        <div className="modal-overlay" onClick={() => setShowWifiModal(false)}>
          <div className="url-modal wifi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="url-header">
              <span>📶 WiFi - {selectedTabletForWifi.name}</span>
              <button className="url-close" onClick={() => setShowWifiModal(false)}>×</button>
            </div>
            <div className="url-body">
              <div className="url-info">
                <strong>Rede atual:</strong>
                <div className="current-url">{selectedTabletForWifi.deviceInfo?.wifiName || 'Não informado'}</div>
              </div>

              <div className="wifi-networks-panel">
                <strong>Redes vistas pelo kiosk:</strong>
                {(selectedTabletForWifi.deviceInfo?.availableWifiNetworks || []).length === 0 ? (
                  <div className="wifi-network-empty">Nenhuma rede recebida ainda.</div>
                ) : (
                  <div className="wifi-network-list">
                    {selectedTabletForWifi.deviceInfo.availableWifiNetworks.map((network) => (
                      <button
                        key={`${network.ssid}-${network.bssid || ''}`}
                        className="wifi-network-item"
                        onClick={() => setWifiForm({
                          ...wifiForm,
                          ssid: network.ssid,
                          securityType: network.securityType || 'WPA-PSK'
                        })}
                      >
                        <span>{network.ssid}</span>
                        <small>{network.securityType || 'OPEN'} | {network.level} dBm</small>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="url-form-group">
                <label>SSID:</label>
                <input
                  type="text"
                  className="url-input"
                  value={wifiForm.ssid}
                  onChange={(e) => setWifiForm({...wifiForm, ssid: e.target.value})}
                  placeholder="Nome da rede"
                />
              </div>
              <div className="url-form-group">
                <label>Tipo de senha:</label>
                <select
                  className="url-input"
                  value={wifiForm.securityType}
                  onChange={(e) => setWifiForm({...wifiForm, securityType: e.target.value})}
                >
                  <option value="WPA-PSK">WPA/WPA2-PSK</option>
                  <option value="WPA3-SAE">WPA3-SAE</option>
                  <option value="WEP">WEP</option>
                  <option value="OPEN">Aberta</option>
                </select>
              </div>
              <div className="url-form-group">
                <label>Senha:</label>
                <input
                  type="password"
                  className="url-input"
                  value={wifiForm.password}
                  onChange={(e) => setWifiForm({...wifiForm, password: e.target.value})}
                  placeholder={wifiForm.securityType === 'OPEN' ? 'Rede aberta' : 'Senha da rede'}
                  disabled={wifiForm.securityType === 'OPEN'}
                />
              </div>

              <div className="url-footer">
                <span className="url-hint">O comando será enviado imediatamente ao dispositivo conectado.</span>
                <div className="url-actions">
                  <button className="btn-url-cancel" onClick={() => handleSendWifiCommand('disconnect')}>
                    Desconectar
                  </button>
                  <button className="btn-url-save" onClick={() => handleSendWifiCommand('connect')} disabled={!wifiForm.ssid.trim()}>
                    Conectar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPhotosModal && selectedTabletForPhotos && (
        <div className="modal-overlay" onClick={() => setShowPhotosModal(false)}>
          <div className="photos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="url-header">
              <span>📷 Fotos - {selectedTabletForPhotos.name}</span>
              <button className="url-close" onClick={() => setShowPhotosModal(false)}>×</button>
            </div>
            <div className="photos-body">
              <div className="photos-toolbar">
                <button className="btn-url-save" onClick={handleRequestPhoto}>
                  Tirar nova foto
                </button>
                <button className="btn-url-cancel" onClick={() => loadTabletPhotos(selectedTabletForPhotos.code)}>
                  Atualizar
                </button>
                <button className="btn-photo-delete-all" onClick={handleDeleteAllPhotos} disabled={tabletPhotos.length === 0}>
                  Remover todas
                </button>
              </div>

              {loadingPhotos ? (
                <div className="photos-empty">Carregando fotos...</div>
              ) : tabletPhotos.length === 0 ? (
                <div className="photos-empty">Nenhuma foto solicitada ainda</div>
              ) : (
                <div className="photos-grid">
                  {tabletPhotos.map(photo => (
                    <div key={photo._id} className={`photo-card photo-${photo.status}`}>
                      {getPhotoImageUrl(photo) ? (
                        <img src={getPhotoImageUrl(photo)} alt={`Captura ${formatDate(photo.capturedAt || photo.createdAt)}`} />
                      ) : (
                        <div className="photo-placeholder">{photo.status === 'pending' ? 'Pendente' : photo.url ? 'Carregando imagem' : 'Sem imagem'}</div>
                      )}
                      <div className="photo-meta">
                        <strong>{photo.status === 'captured' ? 'Capturada' : photo.status === 'failed' ? 'Falhou' : 'Pendente'}</strong>
                        <span>{formatDate(photo.capturedAt || photo.createdAt)}</span>
                        <span>{photo.source === 'power_disconnect' ? 'Automática: desconectou da energia' : 'Manual'}</span>
                        {photo.message && <small>{photo.message}</small>}
                        <button className="btn-photo-delete" onClick={() => handleDeletePhoto(photo)}>
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPrintscreenModal && selectedTabletForPrintscreen && (
        <div className="modal-overlay" onClick={() => setShowPrintscreenModal(false)}>
          <div className="photos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="url-header">
              <span>PrintScreen - {selectedTabletForPrintscreen.name}</span>
              <button className="url-close" onClick={() => setShowPrintscreenModal(false)}>×</button>
            </div>
            <div className="photos-body">
              <div className="photos-toolbar">
                <button className="btn-url-save" onClick={handleRequestPrintscreen}>
                  Tirar PrintScreen
                </button>
                <button className="btn-url-cancel" onClick={() => loadTabletPrintscreens(selectedTabletForPrintscreen.code)}>
                  Atualizar
                </button>
                <button className="btn-photo-delete-all" onClick={handleDeleteAllPrintscreens} disabled={tabletPrintscreens.length === 0}>
                  Remover todos
                </button>
              </div>

              {loadingPrintscreens ? (
                <div className="photos-empty">Carregando printscreens...</div>
              ) : tabletPrintscreens.length === 0 ? (
                <div className="photos-empty">Nenhum printscreen solicitado ainda</div>
              ) : (
                <div className="photos-grid">
                  {tabletPrintscreens.map(printscreen => (
                    <div key={printscreen._id} className={`photo-card photo-${printscreen.status}`}>
                      {getPrintscreenImageUrl(printscreen) ? (
                        <img src={getPrintscreenImageUrl(printscreen)} alt={`PrintScreen ${formatDate(printscreen.capturedAt || printscreen.createdAt)}`} />
                      ) : (
                        <div className="photo-placeholder">{printscreen.status === 'pending' ? 'Pendente' : printscreen.url ? 'Carregando imagem' : 'Sem imagem'}</div>
                      )}
                      <div className="photo-meta">
                        <strong>{printscreen.status === 'captured' ? 'Capturado' : printscreen.status === 'failed' ? 'Falhou' : 'Pendente'}</strong>
                        <span>{formatDate(printscreen.capturedAt || printscreen.createdAt)}</span>
                        {printscreen.message && <small>{printscreen.message}</small>}
                        <button className="btn-photo-delete" onClick={() => handleDeletePrintscreen(printscreen)}>
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showUrlModal && selectedTabletForUrl && (
        <div className="modal-overlay" onClick={() => setShowUrlModal(false)}>
          <div className="url-modal" onClick={(e) => e.stopPropagation()}>
            <div className="url-header">
              <span>🔗 Configurar URL - {selectedTabletForUrl.name}</span>
              <button className="url-close" onClick={() => setShowUrlModal(false)}>×</button>
            </div>
            <div className="url-body">
              <div className="url-info">
                <strong>URL Atual:</strong>
                <div className="current-url">
                  {selectedTabletForUrl.homepage_url || '(Nenhuma URL configurada)'}
                </div>
              </div>
              <div className="url-form-group">
                <label>Nova URL:</label>
                <input 
                  type="text"
                  className="url-input"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://exemplo.com"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdateUrl();
                    }
                  }}
                />
              </div>
              <div className="url-footer">
                <span className="url-hint">A URL será enviada imediatamente ao dispositivo conectado.</span>
                <div className="url-actions">
                  <button className="btn-url-cancel" onClick={() => setShowUrlModal(false)}>
                    Cancelar
                  </button>
                  <button className="btn-url-save" onClick={handleUpdateUrl} disabled={!newUrl.trim()}>
                    💾 Salvar URL
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAutoReloadModal && selectedTabletForAutoReload && (
        <div className="modal-overlay" onClick={() => setShowAutoReloadModal(false)}>
          <div className="url-modal" onClick={(e) => e.stopPropagation()}>
            <div className="url-header">
              <span>Configurar Auto-reload - {selectedTabletForAutoReload.name}</span>
              <button className="url-close" onClick={() => setShowAutoReloadModal(false)}>X</button>
            </div>
            <div className="url-body">
              <div className="url-form-group">
                <label>Intervalo em minutos:</label>
                <input
                  type="number"
                  className="url-input"
                  value={autoReloadInterval}
                  onChange={(e) => setAutoReloadInterval(e.target.value)}
                  min="0"
                  placeholder="0"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveAutoReload();
                  }}
                />
              </div>
              <div className="url-footer">
                <span className="url-hint">Use 0 para desativar o auto-reload.</span>
                <div className="url-actions">
                  <button className="btn-url-cancel" onClick={() => setShowAutoReloadModal(false)}>
                    Cancelar
                  </button>
                  <button className="btn-url-save" onClick={handleSaveAutoReload}>
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBrightnessModal && selectedTabletForBrightness && (
        <div className="modal-overlay" onClick={() => setShowBrightnessModal(false)}>
          <div className="url-modal" onClick={(e) => e.stopPropagation()}>
            <div className="url-header">
              <span>Brilho da tela - {selectedTabletForBrightness.name}</span>
              <button className="url-close" onClick={() => setShowBrightnessModal(false)}>X</button>
            </div>
            <div className="url-body">
              <div className="url-form-group">
                <label>Brilho: {screenBrightness}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={screenBrightness}
                  onChange={(e) => setScreenBrightness(Number(e.target.value))}
                  className="brightness-slider"
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={screenBrightness}
                  onChange={(e) => setScreenBrightness(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="url-input"
                />
              </div>
              <div className="url-footer">
                <span className="url-hint">O brilho será aplicado ao sistema do tablet.</span>
                <div className="url-actions">
                  <button className="btn-url-cancel" onClick={() => setShowBrightnessModal(false)}>
                    Cancelar
                  </button>
                  <button className="btn-url-save" onClick={handleSetBrightness}>
                    Aplicar brilho
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAutoCodModal && selectedTabletForAutoCod && (
        <div className="modal-overlay" onClick={() => setShowAutoCodModal(false)}>
          <div className="url-modal auto-cod-modal" onClick={(e) => e.stopPropagation()}>
            <div className="url-header">
              <span>Auto Cod - {selectedTabletForAutoCod.name}</span>
              <button className="url-close" onClick={() => setShowAutoCodModal(false)}>X</button>
            </div>
            <div className="url-body">
              <div className="url-form-group">
                <label>Link detectado na pagina atual:</label>
                <input
                  type="text"
                  className="url-input"
                  value={autoCodForm.triggerUrl}
                  onChange={(e) => setAutoCodForm(prev => ({ ...prev, triggerUrl: e.target.value }))}
                  placeholder="Ex: /login, ponto.sistema.gov.br ou https://..."
                />
              </div>
              <div className="url-form-group">
                <label>Comando salvo que sera executado:</label>
                <select
                  className="url-input"
                  value={autoCodForm.savedCommandId}
                  onChange={(e) => setAutoCodForm(prev => ({ ...prev, savedCommandId: e.target.value }))}
                  disabled={savedCommands.length === 0}
                >
                  {savedCommands.length === 0 ? (
                    <option value="">Nenhum comando salvo encontrado</option>
                  ) : (
                    <>
                      <option value="">Selecione um comando salvo</option>
                      {savedCommands.map(command => (
                        <option key={command._id} value={command._id}>
                          {command.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div className="url-footer">
                <span className="url-hint">
                  Quando a URL real do kiosk contiver esse link, o comando salvo sera enviado ao Console JS.
                </span>
                <div className="url-actions">
                  <button className="btn-url-cancel" onClick={() => setShowAutoCodModal(false)}>
                    Fechar
                  </button>
                  <button
                    className="btn-url-save"
                    onClick={handleAddAutoCodRule}
                    disabled={savedCommands.length === 0 || !autoCodForm.savedCommandId}
                  >
                    Adicionar
                  </button>
                </div>
              </div>

              <div className="auto-cod-list">
                <div className="auto-cod-list-header">Regras configuradas</div>
                {loadingAutoCodRules ? (
                  <div className="saved-commands-empty">Carregando regras...</div>
                ) : autoCodRules.length === 0 ? (
                  <div className="saved-commands-empty">Nenhuma regra Auto Cod cadastrada.</div>
                ) : (
                  autoCodRules.map(rule => (
                    <div key={rule._id} className={`auto-cod-rule ${rule.enabled ? '' : 'auto-cod-rule-disabled'}`}>
                      <div className="auto-cod-rule-info">
                        <strong>{rule.triggerUrl}</strong>
                        <span>{rule.savedCommandName || 'Comando salvo'}</span>
                        {rule.lastTriggeredAt && (
                          <small>
                            Ultimo disparo: {formatDate(rule.lastTriggeredAt)}
                          </small>
                        )}
                      </div>
                      <div className="auto-cod-rule-actions">
                        <button
                          className="btn-url-cancel"
                          onClick={() => handleToggleAutoCodRule(rule)}
                        >
                          {rule.enabled ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          className="btn-delete-saved"
                          onClick={() => handleDeleteAutoCodRule(rule)}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="modal-overlay" onClick={() => { setShowBulkModal(false); setShowBulkSavedCommands(false); }}>
          <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bulk-header">
              <span>📦 Ações em lote</span>
              <button className="bulk-close" onClick={() => { setShowBulkModal(false); setShowBulkSavedCommands(false); }}>×</button>
            </div>
            <div className="bulk-body">
              <div className="bulk-left">
                <div className="bulk-selection-actions">
                  <button className="bulk-select-btn" onClick={() => bulkSelectAll(filteredTablets.map(t => t.code))}>
                    Marcar todos
                  </button>
                  <button className="bulk-select-btn" onClick={bulkClearSelection}>
                    Desmarcar todos
                  </button>
                  <div className="bulk-selected-count">
                    Selecionados: <strong>{bulkSelectedCodes.length}</strong>
                  </div>
                </div>
                <div className="bulk-devices-list">
                  {filteredTablets.map(t => (
                    <label key={t.code} className="bulk-device-item">
                      <input
                        type="checkbox"
                        checked={bulkSelectedCodes.includes(t.code)}
                        onChange={() => toggleBulkSelected(t.code)}
                      />
                      <span className={`bulk-status-dot ${t.status}`} title={t.status === 'online' ? 'Online' : 'Offline'} />
                      <span className="bulk-device-name">{t.name}</span>
                      <span className="bulk-device-code">{t.code}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="bulk-right">
                <div className="bulk-tabs">
                  <button className={`bulk-tab ${bulkActiveAction === 'reload' ? 'active' : ''}`} onClick={() => setBulkActiveAction('reload')}>🔄 Reload</button>
                  <button className={`bulk-tab ${bulkActiveAction === 'js' ? 'active' : ''}`} onClick={() => setBulkActiveAction('js')}>📜 JS</button>
                  <button className={`bulk-tab ${bulkActiveAction === 'url' ? 'active' : ''}`} onClick={() => setBulkActiveAction('url')}>🔗 URL</button>
                  <button className={`bulk-tab ${bulkActiveAction === 'clear' ? 'active' : ''}`} onClick={() => setBulkActiveAction('clear')}>🗑️ Limpar</button>
                </div>

                {bulkActiveAction === 'reload' && (
                  <div className="bulk-panel">
                    <div className="bulk-panel-title">Enviar reload para os tablets selecionados</div>
                    <button className="bulk-primary btn-reload" onClick={bulkSendReload} disabled={bulkSending}>
                      {bulkSending ? '⏳ Enviando...' : '🔄 Enviar Reload'}
                    </button>
                    <div className="bulk-hint">O reload será enviado imediatamente aos dispositivos conectados.</div>
                  </div>
                )}

                {bulkActiveAction === 'js' && (
                  <div className="bulk-panel">
                    <div className="bulk-panel-title">Enviar comando JavaScript para os tablets selecionados</div>
                    <div className="bulk-js-toolbar">
                      <button className="btn-console-saved" onClick={() => setShowBulkSavedCommands(!showBulkSavedCommands)}>
                        📋 Comandos Salvos ({savedCommands.length})
                      </button>
                    </div>
                    <textarea
                      className="bulk-textarea"
                      value={bulkJsCommand}
                      onChange={(e) => setBulkJsCommand(e.target.value)}
                      placeholder="console.log('Hello');\nalert('Teste');"
                      rows="8"
                    />
                    <button className="bulk-primary btn-js" onClick={bulkSendJs} disabled={bulkSending || !bulkJsCommand.trim()}>
                      {bulkSending ? '⏳ Enviando...' : '📜 Enviar JS'}
                    </button>
                    <div className="bulk-hint">O JS será enviado imediatamente aos dispositivos conectados.</div>

                    {showBulkSavedCommands && (
                      <div className="saved-commands-list bulk-saved-commands">
                        <div className="saved-commands-header">📋 Comandos Salvos</div>
                        {savedCommands.length === 0 ? (
                          <div className="saved-commands-empty">Nenhum comando salvo ainda</div>
                        ) : (
                          <div className="saved-commands-items">
                            {savedCommands.map(cmd => (
                              <div key={cmd._id} className="saved-command-item">
                                <div className="saved-command-info" onClick={() => handleLoadCommandIntoBulk(cmd)}>
                                  <div className="saved-command-name">{cmd.name}</div>
                                  <div className="saved-command-preview">{cmd.command.substring(0, 60)}{cmd.command.length > 60 ? '...' : ''}</div>
                                </div>
                                <button className="btn-delete-saved" onClick={() => handleDeleteSavedCommand(cmd._id)} title="Deletar comando">
                                  🗑️
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {bulkActiveAction === 'url' && (
                  <div className="bulk-panel">
                    <div className="bulk-panel-title">Atualizar URL para os tablets selecionados</div>
                    <input
                      type="text"
                      className="bulk-input"
                      value={bulkUrl}
                      onChange={(e) => setBulkUrl(e.target.value)}
                      placeholder="https://exemplo.com"
                    />
                    <button className="bulk-primary btn-url" onClick={bulkSendUrl} disabled={bulkSending || !bulkUrl.trim()}>
                      {bulkSending ? '⏳ Enviando...' : '🔗 Enviar URL'}
                    </button>
                    <div className="bulk-hint">A URL será enviada imediatamente aos dispositivos conectados.</div>
                  </div>
                )}

                {bulkActiveAction === 'clear' && (
                  <div className="bulk-panel">
                    <div className="bulk-panel-title">Limpar LocalStorage/Cookies/Cache nos tablets selecionados</div>
                    <button className="bulk-primary btn-clear-storage" onClick={bulkSendClearStorage} disabled={bulkSending}>
                      {bulkSending ? '⏳ Enviando...' : '🗑️ Solicitar limpeza'}
                    </button>
                    <div className="bulk-hint">A limpeza será enviada imediatamente aos dispositivos conectados.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && selectedTabletForPassword && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="url-modal" onClick={(e) => e.stopPropagation()} style={{ borderColor: '#9C27B0' }}>
            <div className="url-header" style={{ borderBottomColor: '#9C27B0' }}>
              <span style={{ color: '#9C27B0' }}>🔑 Trocar Senha Admin — {selectedTabletForPassword.name}</span>
              <button className="url-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <div className="url-body">
              <div className="url-info" style={{ borderLeftColor: '#9C27B0', background: 'rgba(156,39,176,0.08)' }}>
                <strong>Dispositivo:</strong> {selectedTabletForPassword.name}
                <br />
                <small style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem' }}>
                  A nova senha será enviada imediatamente ao dispositivo conectado.
                </small>
              </div>
              <div className="url-form-group">
                <label>Nova senha admin:</label>
                <input
                  type="password"
                  className="url-input"
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  placeholder="Digite a nova senha"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSetPassword(); }}
                />
              </div>
              <div className="url-footer">
                <span className="url-hint">💡 Senha padrão do aparelho: 0000</span>
                <div className="url-actions">
                  <button className="btn-url-cancel" onClick={() => setShowPasswordModal(false)}>
                    Cancelar
                  </button>
                  <button
                    className="btn-url-save"
                    style={{ background: '#9C27B0', borderColor: '#9C27B0' }}
                    onClick={handleSetPassword}
                    disabled={!newAdminPassword.trim()}
                  >
                    🔑 Enviar Senha
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditTabletModal && (
        <div className="modal-overlay" onClick={() => setShowEditTabletModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Editar dispositivo</h2>
            <form onSubmit={handleUpdateTabletDetails}>
              <div className="form-group">
                <label>Codigo do dispositivo *</label>
                <input
                  type="text"
                  value={editingTablet.code}
                  onChange={(e) => setEditingTablet({...editingTablet, code: e.target.value.toUpperCase()})}
                  maxLength="10"
                  pattern="[A-Z0-9]{10}"
                  title="Codigo deve ter 10 caracteres alfanumericos"
                  required
                />
                <small style={{color: '#999', fontSize: '12px'}}>
                  Ao trocar o codigo, fotos, prints e logs continuam vinculados ao dispositivo.
                </small>
              </div>
              <div className="form-group">
                <label>Nome do tablet *</label>
                <input
                  type="text"
                  value={editingTablet.name}
                  onChange={(e) => setEditingTablet({...editingTablet, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Local</label>
                <input
                  type="text"
                  value={editingTablet.location}
                  onChange={(e) => setEditingTablet({...editingTablet, location: e.target.value})}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowEditTabletModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Salvar alteracoes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Cadastrar Novo Tablet</h2>
            <form onSubmit={handleAddTablet}>
              <div className="form-group">
                <label>Código do Dispositivo (opcional)</label>
                <input
                  type="text"
                  value={newTablet.code}
                  onChange={(e) => setNewTablet({...newTablet, code: e.target.value.toUpperCase()})}
                  placeholder="Ex: A3F7B2E9D1 (deixe vazio para gerar automaticamente)"
                  maxLength="10"
                  pattern="[A-Z0-9]{10}|"
                  title="Código deve ter 10 caracteres alfanuméricos ou deixe vazio"
                />
                <small style={{color: '#666', fontSize: '12px'}}>
                  💡 Digite o código exibido no app Android ou deixe vazio para gerar novo
                </small>
              </div>
              <div className="form-group">
                <label>Nome do Tablet *</label>
                <input
                  type="text"
                  value={newTablet.name}
                  onChange={(e) => setNewTablet({...newTablet, name: e.target.value})}
                  placeholder="Ex: Tablet Recepção"
                  required
                />
              </div>
              <div className="form-group">
                <label>Local</label>
                <input
                  type="text"
                  value={newTablet.location}
                  onChange={(e) => setNewTablet({...newTablet, location: e.target.value})}
                  placeholder="Ex: Sala 101"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowAddModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Cadastrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
